import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, Phone, Send, Brain, DollarSign, Handshake, X, Shield, MessageSquare, ArrowLeft, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { roomsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const STUN_SERVERS = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }] };

export default function DealRoomPage() {
  const { id: roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [proposePrice, setProposePrice] = useState('');
  const [aiQuery, setAiQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPropose, setShowPropose] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Video state
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const chatEndRef = useRef(null);

  const isbuyer = room?.buyer_id === user?.id;
  const myRole = isbuyer ? 'Buyer' : 'Seller';

  // Load room data
  useEffect(() => {
    if (!roomId) return;
    roomsAPI.get(roomId).then(res => {
      setRoom(res.data);
      setMessages(res.data.messages || []);
      setLoading(false);
    }).catch(() => { toast.error('Room not found'); navigate('/rooms'); });
  }, [roomId, navigate]);

  // WebSocket connection
  useEffect(() => {
    if (!roomId || !user) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL.replace(/^https?:\/\//, '');
    const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/ws/room/${roomId}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'chat':
          setMessages(prev => [...prev, { id: Date.now(), type: 'chat', sender: data.sender, text: data.text, timestamp: new Date().toISOString() }]);
          break;
        case 'ai_message':
          setMessages(prev => [...prev, { id: Date.now(), type: 'ai', sender: 'AI Assistant', text: data.text, timestamp: new Date().toISOString() }]);
          break;
        case 'proposal':
          setMessages(prev => [...prev, { id: Date.now(), type: 'proposal', sender: 'System', text: data.message, timestamp: new Date().toISOString() }]);
          setRoom(prev => prev ? { ...prev, current_offer: data.price, offered_by: data.by } : prev);
          break;
        case 'deal_made':
          setRoom(prev => prev ? { ...prev, status: 'deal_made', deal_price: data.price, order_id: data.order_id } : prev);
          toast.success(`Deal made at ${formatCurrency(data.price)}!`);
          break;
        case 'room_closed':
          setRoom(prev => prev ? { ...prev, status: 'closed' } : prev);
          toast.info('Room has been closed');
          break;
        case 'peer_joined':
          setPeerConnected(true);
          break;
        case 'peer_left':
          setPeerConnected(false);
          cleanupCall();
          break;
        case 'offer':
          handleOffer(data);
          break;
        case 'answer':
          handleAnswer(data);
          break;
        case 'ice-candidate':
          handleIceCandidate(data);
          break;
        default: break;
      }
    };

    const interval = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    return () => { clearInterval(interval); ws.close(); };
  }, [roomId, user]);

  // Auto-scroll chat
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ====== WebRTC ======
  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setVideoEnabled(true);
      setInCall(true);

      const pc = new RTCPeerConnection(STUN_SERVERS);
      peerRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
      };
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') cleanupCall();
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
    } catch (e) {
      toast.error('Camera/microphone access denied');
    }
  };

  const handleOffer = async (data) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setVideoEnabled(true);
      setInCall(true);

      const pc = new RTCPeerConnection(STUN_SERVERS);
      peerRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
      };
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: data.sdp }));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsRef.current?.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
    } catch (e) {
      toast.error('Failed to join video call');
    }
  };

  const handleAnswer = async (data) => {
    if (peerRef.current) {
      await peerRef.current.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: data.sdp }));
    }
  };

  const handleIceCandidate = async (data) => {
    if (peerRef.current && data.candidate) {
      try { await peerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch (e) {}
    }
  };

  const toggleVideo = () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
    }
  };

  const toggleAudio = () => {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
    }
  };

  const cleanupCall = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    peerRef.current?.close();
    peerRef.current = null;
    streamRef.current = null;
    setInCall(false);
    setVideoEnabled(false);
  };

  const endCall = () => {
    cleanupCall();
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
  };

  // ====== Chat & Actions ======
  const sendMessage = () => {
    if (!inputMsg.trim() || !wsRef.current) return;
    const msg = { type: 'chat', sender: user.name || myRole, text: inputMsg, userId: user.id };
    wsRef.current.send(JSON.stringify(msg));
    setMessages(prev => [...prev, { id: Date.now(), type: 'chat', sender: user.name || myRole, text: inputMsg, timestamp: new Date().toISOString(), mine: true }]);
    setInputMsg('');
  };

  const handlePropose = async () => {
    const price = parseFloat(proposePrice);
    if (isNaN(price) || price <= 0) return;
    try {
      await roomsAPI.propose(roomId, { proposed_price: price });
      setProposePrice('');
      setShowPropose(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAccept = async () => {
    try {
      const res = await roomsAPI.accept(roomId);
      toast.success(`Deal made at ${formatCurrency(res.data.deal_price)}!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleClose = async () => {
    try {
      await roomsAPI.close(roomId);
      endCall();
    } catch (e) { toast.error('Failed to close room'); }
  };

  const handleAIAssist = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const res = await roomsAPI.aiAssist(roomId, aiQuery);
      setMessages(prev => [...prev, { id: Date.now(), type: 'ai', sender: 'AI Assistant', text: res.data.advice, timestamp: new Date().toISOString() }]);
      setAiQuery('');
    } catch (e) { toast.error('AI unavailable'); }
    finally { setAiLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!room) return null;

  const isDealDone = room.status === 'deal_made';
  const isClosed = room.status === 'closed';
  const canAccept = room.current_offer && room.offered_by !== user?.id && !isDealDone && !isClosed;

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="deal-room">
      {/* Top Bar */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/rooms"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white"><ArrowLeft size={18} /></Button></Link>
          <div>
            <h1 className="font-display font-bold text-white text-sm sm:text-base">{room.item_name}</h1>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span>Listed: {formatCurrency(room.listing_price)}</span>
              {room.current_offer && <span className="text-[#b8ff00]">Current offer: {formatCurrency(room.current_offer)}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-green-900/40 rounded-full border border-green-700/30">
            <Shield size={12} className="text-green-400" />
            <span className="text-[10px] text-green-400 font-medium">E2E Encrypted</span>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${isDealDone ? 'bg-green-500/20 text-green-400' : isClosed ? 'bg-red-500/20 text-red-400' : 'bg-[#b8ff00]/20 text-[#b8ff00]'}`}>
            {isDealDone ? 'Deal Made' : isClosed ? 'Closed' : 'Negotiating'}
          </span>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Video Section */}
        <div className="lg:w-2/5 bg-zinc-900 p-3 flex flex-col">
          <div className="relative flex-1 rounded-xl overflow-hidden bg-zinc-800 min-h-[200px]">
            <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
            {!inCall && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-20 h-20 bg-zinc-700 rounded-full flex items-center justify-center mb-4">
                  <Video size={32} className="text-zinc-400" />
                </div>
                <p className="text-zinc-400 text-sm mb-3">Start a video call to negotiate face-to-face</p>
                {!isDealDone && !isClosed && (
                  <Button onClick={startCall} className="bg-[#b8ff00] text-black font-bold rounded-full px-6" data-testid="start-call-btn">
                    <Video size={16} className="mr-2" /> Start Video Call
                  </Button>
                )}
              </div>
            )}
            {/* Local video PiP */}
            {inCall && (
              <div className="absolute bottom-3 right-3 w-32 h-24 rounded-lg overflow-hidden border-2 border-zinc-600 bg-zinc-800">
                <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Call Controls */}
          {inCall && (
            <div className="flex items-center justify-center gap-3 mt-3">
              <Button onClick={toggleVideo} variant="ghost" className={`w-10 h-10 rounded-full ${videoEnabled ? 'bg-zinc-700 text-white' : 'bg-red-500/20 text-red-400'}`} data-testid="toggle-video">
                {videoEnabled ? <Video size={18} /> : <VideoOff size={18} />}
              </Button>
              <Button onClick={toggleAudio} variant="ghost" className={`w-10 h-10 rounded-full ${audioEnabled ? 'bg-zinc-700 text-white' : 'bg-red-500/20 text-red-400'}`} data-testid="toggle-audio">
                {audioEnabled ? <Mic size={18} /> : <MicOff size={18} />}
              </Button>
              <Button onClick={endCall} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white" data-testid="end-call-btn">
                <Phone size={18} className="rotate-[135deg]" />
              </Button>
            </div>
          )}

          {/* Participants */}
          <div className="mt-3 flex items-center gap-3 px-2">
            <div className="flex items-center gap-2 bg-zinc-800 rounded-full px-3 py-1.5">
              <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center"><span className="text-xs text-cyan-400">{room.buyer_name?.[0]}</span></div>
              <span className="text-xs text-zinc-300">{room.buyer_name} <span className="text-zinc-500">(Buyer)</span></span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-800 rounded-full px-3 py-1.5">
              <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center"><span className="text-xs text-amber-400">{room.seller_name?.[0]}</span></div>
              <span className="text-xs text-zinc-300">{room.seller_name} <span className="text-zinc-500">(Seller)</span></span>
            </div>
          </div>
        </div>

        {/* Chat + Actions Section */}
        <div className="flex-1 flex flex-col min-h-0 bg-zinc-950 border-l border-zinc-800">
          {/* Deal Banner */}
          {isDealDone && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }}
              className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-b border-green-500/20 px-4 py-3">
              <div className="flex items-center gap-3">
                <Handshake className="text-green-400" size={20} />
                <div>
                  <p className="text-green-400 font-semibold text-sm">Deal Closed at {formatCurrency(room.deal_price)}</p>
                  <Link to={`/orders`} className="text-xs text-green-400/70 underline">View order →</Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-testid="chat-messages">
            {messages.map((msg, i) => {
              const isMine = msg.mine || (msg.sender === (user?.name || myRole));
              if (msg.type === 'system' || msg.type === 'deal') {
                return (
                  <div key={msg.id || i} className="flex justify-center">
                    <span className={`text-xs px-3 py-1.5 rounded-full ${msg.type === 'deal' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-400'}`}>
                      {msg.text}
                    </span>
                  </div>
                );
              }
              if (msg.type === 'proposal') {
                return (
                  <div key={msg.id || i} className="flex justify-center">
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2 text-center">
                      <DollarSign size={14} className="text-amber-400 mx-auto mb-1" />
                      <span className="text-xs text-amber-300">{msg.text}</span>
                    </div>
                  </div>
                );
              }
              if (msg.type === 'ai') {
                return (
                  <div key={msg.id || i} className="flex justify-start">
                    <div className="max-w-[80%] bg-violet-500/10 border border-violet-500/20 rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex items-center gap-2 mb-1"><Brain size={12} className="text-violet-400" /><span className="text-xs font-medium text-violet-400">AI Assistant</span></div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{msg.text}</p>
                    </div>
                  </div>
                );
              }
              return (
                <div key={msg.id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${isMine ? 'bg-[#b8ff00] text-black rounded-br-sm' : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'}`}>
                    {!isMine && <p className="text-xs font-medium mb-1 text-zinc-400">{msg.sender}</p>}
                    <p className="text-sm">{msg.text}</p>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          {/* Action Panels */}
          <AnimatePresence>
            {showPropose && !isDealDone && !isClosed && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="border-t border-zinc-800 overflow-hidden">
                <div className="p-3 bg-zinc-900 flex items-center gap-2">
                  <DollarSign size={16} className="text-amber-400" />
                  <Input type="number" placeholder="Your price..." value={proposePrice} onChange={e => setProposePrice(e.target.value)}
                    className="flex-1 bg-zinc-800 border-zinc-700 text-white h-9" data-testid="propose-price-input" />
                  <Button onClick={handlePropose} size="sm" disabled={!proposePrice} className="bg-amber-500 text-black rounded-full text-xs font-bold" data-testid="submit-propose-btn">Propose</Button>
                  <Button onClick={() => setShowPropose(false)} variant="ghost" size="sm" className="text-zinc-400"><X size={14} /></Button>
                </div>
              </motion.div>
            )}
            {showAI && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="border-t border-zinc-800 overflow-hidden">
                <div className="p-3 bg-zinc-900 flex items-center gap-2">
                  <Brain size={16} className="text-violet-400" />
                  <Input placeholder="Ask AI for advice..." value={aiQuery} onChange={e => setAiQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAIAssist()}
                    className="flex-1 bg-zinc-800 border-zinc-700 text-white h-9" data-testid="ai-query-input" />
                  <Button onClick={handleAIAssist} size="sm" disabled={aiLoading || !aiQuery.trim()} className="bg-violet-500 text-white rounded-full text-xs font-bold" data-testid="submit-ai-btn">
                    {aiLoading ? '...' : 'Ask'}
                  </Button>
                  <Button onClick={() => setShowAI(false)} variant="ghost" size="sm" className="text-zinc-400"><X size={14} /></Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bottom Action Bar */}
          {!isDealDone && !isClosed && (
            <div className="border-t border-zinc-800 bg-zinc-900 p-3">
              {/* Accept / Action buttons */}
              {canAccept && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <Handshake size={16} className="text-green-400 flex-shrink-0" />
                  <span className="text-xs text-green-300 flex-1">Offer: <strong>{formatCurrency(room.current_offer)}</strong></span>
                  <Button onClick={handleAccept} size="sm" className="bg-green-500 text-black font-bold rounded-full text-xs" data-testid="accept-deal-btn">
                    <Check size={14} className="mr-1" /> Accept Deal
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Input placeholder="Type a message..." value={inputMsg} onChange={e => setInputMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white h-10 rounded-full px-4" data-testid="chat-input" />
                <Button onClick={sendMessage} size="sm" className="bg-[#b8ff00] text-black rounded-full w-10 h-10" data-testid="send-msg-btn"><Send size={16} /></Button>
                <Button onClick={() => { setShowPropose(!showPropose); setShowAI(false); }} variant="ghost" size="sm" className="text-amber-400 rounded-full w-10 h-10" data-testid="propose-btn"><DollarSign size={16} /></Button>
                <Button onClick={() => { setShowAI(!showAI); setShowPropose(false); }} variant="ghost" size="sm" className="text-violet-400 rounded-full w-10 h-10" data-testid="ai-btn"><Brain size={16} /></Button>
                <Button onClick={handleClose} variant="ghost" size="sm" className="text-red-400 rounded-full w-10 h-10" data-testid="close-room-btn"><X size={16} /></Button>
              </div>
            </div>
          )}

          {/* Closed state */}
          {(isDealDone || isClosed) && (
            <div className="border-t border-zinc-800 bg-zinc-900 p-4 text-center">
              <p className="text-sm text-zinc-400 mb-3">{isDealDone ? 'This deal has been completed.' : 'This room has been closed.'}</p>
              <Link to="/orders"><Button className="bg-[#b8ff00] text-black rounded-full font-bold" data-testid="view-orders-btn">View Orders</Button></Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
